import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, from, map, Observable, of, switchMap, tap, timeout, interval } from 'rxjs';
import { User, UserService } from '../core/user/user.service';
import { AuthenticationService, RegisterData } from '../core/auth/authentication-service';
import { DataStorage } from '../core/data/data-storage';
import { Business } from '../core/business/business';
import { getApiUrl } from '../core/helpers/rest-utils';
import { requiresRegistrationCard, SignupVariant } from '../core/helpers/signup-variant';
import { QueryParamsHandling, Router } from '@angular/router';
import { CustomerLocation } from './google-lookup/google-lookup.component';
import { InternalAnalyticsService } from '../core/internal-analytics.service';
import { RegistrationPlatform } from '../core/registration-platform';
import { SessionService } from '../core/session.service';
declare const google: any;

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private static readonly EXPERIMENT_STORAGE_KEY = 'signupExperiment';
  private static readonly SIGNUP_VARIANTS: SignupVariant[] = ['A', 'B', 'C', 'D'];

  emailForVerification: string;
  autocompleteService: any;
  placesService: any;
  userLocation: any = null;
  isEmailVerified = false;
  userLocationLatLon: { lat: number | string, lon: number | string } = { lat: 45.5152, lon: -122.6784 };

  storedGooglePlace: any;
  selectedPlaceId: string;
  activeOnboardingStep: OnboardingStep = OnboardingStep.GoogleLookup;
  storedLead: OnboardingLead;
  persistentStorage = DataStorage.persistent();

  // Variants C/D ("skip the signup flow") are resolved from the public experiment
  // endpoint before the onboarding shell renders, so the flow can start directly on
  // the register step. Sticky per visitor: the API assigns via a long-lived cookie and
  // the resolved assignment is mirrored into persistent storage, so a slow endpoint
  // reuses the known variant rather than flipping the visitor to the control flow.
  skipSignupFlow = false;
  experimentVariant: SignupVariant = 'A';
  private experimentResolved = false;
  private cachedExperiment: SignupExperiment = { skipSignupFlow: false, variant: 'A' };

  prefilledData: OnboardingPrefilledData = {} as OnboardingPrefilledData;

  emailVerificationActive = false;

  private emailValidationInProgress = false;
  private emailValidationMessages = [
    "Checking your email format",
    "Verifying your email with our system",
    "Matching your email with trusted sources",
    "Confirming your email's accuracy",
    "Finalizing your email validation"
  ];
  // Variant D goes straight from this loader to the card step, so the email-
  // validation fiction reads oddly there — describe what the register call is
  // actually doing instead. A/B keep the original copy.
  private accountCreationMessages = [
    "Creating your account",
    "Setting up your workspace",
    "Preparing your 7-day free trial",
    "Getting the secure payment form ready",
    "Finalizing your account setup"
  ];
  // Variant C creates the account then goes to email verification — no payment
  // step — so swap the payment line for the verification email being sent.
  private accountCreationMessagesNoCard = [
    "Creating your account",
    "Setting up your workspace",
    "Preparing your 7-day free trial",
    "Sending your verification email",
    "Finalizing your account setup"
  ];
  currentValidationMessageIndex = 0;
  private messageRotationSubscription: any;

  constructor(private http: HttpClient, private user: UserService, private auth: AuthenticationService, private router: Router, private analytics: InternalAnalyticsService, private session: SessionService) {
    this.fetchLocation();
    // Root singleton: the verification-takeover flags from a previous account
    // would otherwise survive a logout and show the old account's "we sent an
    // email" screen to whoever signs up next by in-app navigation.
    this.session.terminate.subscribe(() => {
      this.emailVerificationActive = false;
      this.isEmailVerified = false;
    });
  }

  get userData(): User {
    return this.user.userData;
  }

  get activeStep(): OnboardingStep {
    return this.activeOnboardingStep;
  }

  // The flow's entry step: the skip-flow variants (C/D) and a mid-flow card refresh
  // both start on the register step; the full flow starts on the Google Business
  // Profile lookup.
  get initialStep(): OnboardingStep {
    if (this.persistentStorage.get('isCollectingRegistrationCard') || this.skipSignupFlow) {
      return OnboardingStep.User;
    }

    return OnboardingStep.GoogleLookup;
  }

  get activeBusiness(): Business {
    return this.user.activeBusiness;
  }

  get selectedPlace() {
    return this.storedGooglePlace || this.persistentStorage.get('googlePlace');
  }

  get completedSteps(): OnboardingStep[] {
    return this.persistentStorage.get('completedSteps') || [];
  }

  get lead(): OnboardingLead {
    return this.storedLead || this.persistentStorage.get('lead') || '';
  }

  get chosenPath(): OnboardingStep {
    return this.persistentStorage.get('chosenPath') || '';
  }

  get isEmailValidationInProgress(): boolean {
    return this.emailValidationInProgress;
  }

  get isEmailVerificationActive() {
    return this.emailVerificationActive;
  }

  get currentValidationMessage(): string {
    const messages = this.skipSignupFlow
      ? (requiresRegistrationCard(this.experimentVariant) ? this.accountCreationMessages : this.accountCreationMessagesNoCard)
      : this.emailValidationMessages;

    return messages[this.currentValidationMessageIndex];
  }

  setLead(lead: OnboardingLead): void {
    if (lead?.id) {
      this.storedLead = lead;
      this.persistentStorage.set('lead', lead)
      this.analytics.setExternalUserUuid(this.lead?.userUuid);
      this.analytics.setLeadUuid(lead.id);
    }
  }

  clearSelectedPlace(): void {
    this.clearSelectedPlaceDetails();
    this.clearSelectedPlaceId();
  }

  clearSelectedPlaceDetails(): void {
    this.storedGooglePlace = null;
    this.persistentStorage.remove('googlePlace');
  }

  clearSelectedPlaceId(): void {
    this.selectedPlaceId = null;
  }

  setChosenPath(step: OnboardingStep): void {
    this.persistentStorage.set('chosenPath', step);
  }

  clearChosenPath(): void {
    this.persistentStorage.remove('chosenPath');
  }

  register(data: RegisterData): Observable<any> {
    data['platform'] = RegistrationPlatform.WEB;

    // A brand-new account is unverified by definition, but this flag is a
    // root-singleton that survives in-session navigation: a verification link for
    // ANOTHER account opened earlier in this SPA session (even a consumed one —
    // the finalize resolver sets the flag on any 2xx) leaves it true. The
    // verification screen auto-advances on it, which would bounce the fresh
    // signup straight to /setup, where the business-less 401 logs them out.
    this.isEmailVerified = false;

    // Same reasoning for the cached user: registering while a previous account's
    // user is still in memory (signup reached by in-app navigation) would let the
    // auth guard route the new account with the old business.
    this.user.clear();

    if (!this.skipSignupFlow && this.lead?.id) {
      data['leadId'] = this.lead.id;
      return this.auth.register(data);
    }

    // The skip-flow variants (C/D) register without a lead, so the pre-account flow
    // that writes campaign/coupon/referral/UTM onto the lead never runs. Attach that
    // bundle to the register body instead; the controller reads each field request-
    // first when no lead is present, so attribution and coupons survive the skipped
    // flow.
    return from(this.analytics.getRegistrationAttribution()).pipe(
      switchMap((attribution) => this.auth.register(Object.assign(data, attribution))),
    );
  }

  // Public, unauthenticated experiment signal. Tells the onboarding shell whether to
  // skip the pre-account flow (Variants C/D) before any lead/user exists. Falls back
  // to this browser's last known assignment, then to the full flow (control), so a
  // slow/down endpoint neither strands the register page nor reshuffles the visitor.
  getExperiment(): Observable<SignupExperiment> {
    if (this.experimentResolved) {
      return of(this.cachedExperiment);
    }

    // Credentialed so the API's long-lived assignment cookie is sent and read back:
    // that cookie is what keeps a visitor on one side of the split across visits.
    return this.http.get(`${getApiUrl()}register/experiment`, { withCredentials: true }).pipe(
      map((res: any) => res?.data),
      map((data: any): SignupExperiment => ({
        skipSignupFlow: !!data?.skipSignupFlow,
        variant: (data?.variant ?? 'A') as SignupVariant,
      })),
      // Generous relative to the endpoint's ~40ms P50: a timeout here silently
      // reroutes the visitor to the control flow (and, while a card-at-creation
      // variant is active, a B-labeled lead), so favor waiting over mislabeling.
      timeout({ first: 5000 }),
      // Mirror the server's assignment locally so the fallback below has something
      // truthful to reach for. Only server-resolved values are stored — never the
      // fail-closed default, which would record an assignment nobody made.
      tap((experiment: SignupExperiment) => this.persistentStorage.set(OnboardingService.EXPERIMENT_STORAGE_KEY, experiment)),
      // A slow or down endpoint must not flip a visitor mid-test: reuse the last
      // assignment this browser saw, and only fall back to control if there is none.
      catchError(() => of(this.persistedExperiment() ?? { skipSignupFlow: false, variant: 'A' as SignupVariant })),
      tap((experiment: SignupExperiment) => {
        this.skipSignupFlow = experiment.skipSignupFlow;
        this.experimentVariant = experiment.variant;
        this.cachedExperiment = experiment;
        this.experimentResolved = true;

        // Skip-flow (Variants C/D) never uses a lead — but one left over from an
        // earlier A/B-era visit would ride the register call / Google redirect and
        // override the variant server-side (a lead's persisted variant always wins),
        // dropping the user into the wrong flow or crashing on an expired scrape.
        if (experiment.skipSignupFlow) {
          this.storedLead = null;
          this.persistentStorage.remove('lead');
        }
      }),
    );
  }

  // The last assignment the API resolved for this browser, or null if none was ever
  // stored (or the stored value is unreadable/unrecognized — a hand-edited entry or a
  // truncated cookie must not put a visitor into a variant that doesn't exist).
  private persistedExperiment(): SignupExperiment | null {
    let stored: Partial<SignupExperiment> | null = null;

    try {
      stored = this.persistentStorage.get(OnboardingService.EXPERIMENT_STORAGE_KEY);
    } catch {
      return null;
    }

    const variant = OnboardingService.SIGNUP_VARIANTS.find(candidate => candidate === stored?.variant);

    return variant ? { skipSignupFlow: !!stored.skipSignupFlow, variant } : null;
  }

  getRegistrationBilling(): Observable<{ publishableKey?: string, clientSecret?: string }> {
    return this.http.post(`${getApiUrl()}register/payment-intent`, {}).pipe(map((res: any) => res?.data));
  }

  storeRegistrationPaymentMethod(paymentMethod: string, couponCode?: string): Observable<any> {
    return this.http.post(`${getApiUrl()}register/payment-method`, { paymentMethod, couponCode }).pipe(map((res: any) => res?.data));
  }

  validateRegistrationCoupon(couponCode: string): Observable<{ code: string, name: string }> {
    return this.http.post(`${getApiUrl()}register/validate-coupon`, { coupon_code: couponCode }).pipe(map((res: any) => res?.data));
  }

  createLead(data: any): Observable<Business> {
    return this.http.post(`${getApiUrl()}leads`, { ...data, platform: RegistrationPlatform.WEB }).pipe(map((lead: any) => lead.data), tap((lead: OnboardingLead) => this.setLead(lead)));
  }

  updateLead(data: any) {
    return this.http.patch(`${getApiUrl()}leads/${this.lead.id}`, data).pipe(map((lead: any) => lead.data), tap((lead: OnboardingLead) => this.setLead(lead)));
  }

  getLead(id?: string): Observable<OnboardingLead> {
    id = id;
    return this.http.get<OnboardingLead>(`${getApiUrl()}leads/${id}`).pipe(map((lead: any) => lead.data));
  }

  getRegistrationLead(): Observable<OnboardingLead> {
    return this.http.get(`${getApiUrl()}register/lead`).pipe(map((lead: any) => lead.data), tap((lead: OnboardingLead) => this.setLead(lead)));
  }

  goToStep(step: OnboardingStep, queryParams?: any, queryParamsHandling: QueryParamsHandling = ''): void {
    this.router.navigate(['/register', { step }], { queryParams, queryParamsHandling: queryParamsHandling }).then(() => {
      document.getElementById('rosieLogo')?.scrollIntoView();
    });
  }

  setSelectedPlace(place: any): void {
    this.storedGooglePlace = place;
    this.persistentStorage.set('googlePlace', place)
  }

  prepareUrl(url: string = ''): string {
    if (url.includes('@')) {
      url = url.split('@').pop() || '';
    }
    return url.includes('https://') || url.includes('http://') ? url : `https://${url}`;
  }

  startEmailValidation(): void {
    this.emailValidationInProgress = true;
    this.currentValidationMessageIndex = 0;

    this.messageRotationSubscription = interval(3000).subscribe(() => {
      if (this.currentValidationMessageIndex < this.emailValidationMessages.length - 1) {
        this.currentValidationMessageIndex++;
      }
    });
  }

  stopEmailValidation(): void {
    this.emailValidationInProgress = false;
    if (this.messageRotationSubscription) {
      this.messageRotationSubscription.unsubscribe();
      this.messageRotationSubscription = null;
    }
  }

  checkEmailVerification(): Observable<any> {
    return this.http.get(`${getApiUrl()}email/verify/status`).pipe(map((res: any) => res.data));
  }

  resendEmailVerification(): Observable<any> {
    return this.http.post(`${getApiUrl()}email/resend-verification-notification`, {}).pipe(map((res: any) => res.data));
  }

  finalizeRegistration(uuid: string, hash: string, signature: string, expires: string): Observable<any> {
    const body = {
      signature,
      expires
    };
    return this.http.post(`${getApiUrl()}register/finalize/${uuid}/${hash}`, body).pipe(map((res: any) => res.data));
  }

  private fetchLocation(): void {
    this.http.get<CustomerLocation>('https://extreme-ip-lookup.com/json/?key=8xTSqsy0DdzfxyHnCQdS')
      .subscribe((location: CustomerLocation) => this.setUserLocationLatLon(location));
  }

  private setUserLocationLatLon(location?: CustomerLocation): void {
    if (!location) {
      this.userLocationLatLon = { lat: 45.5152, lon: -122.6784 };
      return;
    }
    const lat = location?.lat;
    const lon = location?.lon;
    this.userLocationLatLon = { lat, lon };
  }
}

export enum OnboardingStep {
  GoogleLookup = 'google_lookup',
  GoogleLookupVerification = 'google_lookup_verification',
  WebsiteUrl = 'website_url',
  BusinessInfo = 'business_info',
  Agent = 'agent',
  Preview = 'preview',
  User = 'user',
  EmailVerification = 'email'
}
export interface OnboardingPrefilledData {
  websiteUrl: string,
}

export interface SignupExperiment {
  skipSignupFlow: boolean;
  variant: SignupVariant;
}

export interface Clip {
  id: string;
  script: string;
  title: string;
  description: string;
  isPreloadEnabled?: boolean;
}

export interface OnboardingLead {
  id: string;
  businessName: string;
  scrapedAt: Date | null;
  clipsGeneratedAt: Date | null;
  expiresAt: Date | null;
  url: string;
  placeId?: string;
  businessId: string | null;
  isLeadGenerated: boolean;
  clipScripts: Clip[];
  userUuid: string;
  signupVariant?: string;
}
